import type {
  CustomerRow,
  CustomerDetail,
  TenantType,
  TenantStatus,
} from "./types.js";

/** Raw tenant shape as returned by the backend (snake-free Prisma camelCase). */
export interface RawTenant {
  id: string;
  nameEn: string;
  tenantType: TenantType;
  status: TenantStatus;
  phone?: string | null;
  email?: string | null;
  nationality?: string | null;
  corporateLegalNameEn?: string | null;
  corporateCrNumber?: string | null;
  propertyCount?: number | null;
  activeLeases?: number | null;
}

/**
 * Customer display name: corporate legal name when present, else nameEn.
 * Falls back to the id so a row is never blank.
 */
export function customerDisplayName(t: RawTenant): string {
  if (t.tenantType === "CORPORATE" && t.corporateLegalNameEn)
    return t.corporateLegalNameEn;
  return t.nameEn || t.id;
}

/** Map a raw tenant row to a CustomerRow for the list table. */
export function toCustomerRow(t: RawTenant): CustomerRow {
  return {
    id: t.id,
    displayName: customerDisplayName(t),
    primaryPhone: t.phone ?? null,
    primaryEmail: t.email ?? null,
    tenantType: t.tenantType,
    status: t.status,
    propertyCount: t.propertyCount ?? 0,
  };
}

/** Map a raw tenant detail to a CustomerDetail for the profile view. */
export function toCustomerDetail(t: RawTenant): CustomerDetail {
  return {
    id: t.id,
    displayName: customerDisplayName(t),
    nameEn: t.nameEn,
    tenantType: t.tenantType,
    status: t.status,
    primaryPhone: t.phone ?? null,
    primaryEmail: t.email ?? null,
    nationality: t.nationality ?? null,
    corporateLegalNameEn: t.corporateLegalNameEn ?? null,
    corporateCrNumber: t.corporateCrNumber ?? null,
    activeLeases: t.activeLeases ?? 0,
  };
}
